/**
 * Time formatting helpers pinned to Asia/Riyadh.
 *
 * Backend stores all timestamps as UTC ISO strings (server-generated;
 * mobile clock cannot influence them). The frontend MUST format with
 * `timeZone: 'Asia/Riyadh'` so an admin viewing the dashboard from a
 * different timezone (or via VPN) still sees the canonical Saudi time
 * the business operates in.
 */

const TZ = 'Asia/Riyadh';

/**
 * Format a UTC ISO timestamp as full date + time in Riyadh time.
 *
 * @param {string|Date|null|undefined} iso  UTC ISO string or Date
 * @param {'ar'|'en'} language               UI language for the locale
 * @param {object} [opts]                    Extra Intl options override
 */
export const formatRiyadh = (iso, language = 'ar', opts = {}) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...opts,
  });
};

/**
 * Time-only (HH:MM) formatter in Riyadh time. Used in the attendance
 * tables where the date is shown in a separate column. Always 24-hour.
 */
export const formatRiyadhTime = (iso, language = 'en') => {
  if (!iso) return '-';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * Date-only formatter in Riyadh time.
 */
export const formatRiyadhDate = (iso, language = 'ar') => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const RIYADH_TZ = TZ;
